<template>
  <SmDialog
    v-if="settingsDialogOpen"
    v-model:visibility="settingsDialogOpen"
    :title="i18n.t('graphSettings')"
    size="small">
    <p class="mb-4">
      {{ i18n.t('chartDateRangeDesc') }}
    </p>
    <el-date-picker
      v-model="requestedRange"
      type="monthrange"
      range-separator="-"
      :start-placeholder="i18n.t('views.documents.dateRange.from')"
      :end-placeholder="i18n.t('views.documents.dateRange.to')"
      format="MMM. YYYY"
      :clearable="false"
      @update:model-value="settingsDialogOpen = false" />

    <!-- Footer -->
    <template #footer>
      <div class="close-btn">
        <SmButton @click="settingsDialogOpen = false">
          {{ i18n.t('close') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>

  <el-card
    class="information-chart-card"
    :body-style="{
      height: '100%',
      position: 'relative',
    }">
    <!-- Tabs -->
    <el-tabs v-if="!props.hideTabs" v-model="activeTabName">
      <el-tab-pane
        name="account_amount"
        :label="i18n.t('views.dashboard.informationCard.tabs.persons')"
        lazy />
      <el-tab-pane
        name="licence_amount"
        :label="i18n.t('views.dashboard.informationCard.tabs.accounts')"
        lazy />
      <el-tab-pane
        name="software_amount"
        :label="i18n.t('views.dashboard.informationCard.tabs.applications')"
        lazy />
      <el-tab-pane
        name="cost"
        :label="i18n.t('views.dashboard.informationCard.tabs.saasSpends')"
        lazy />
    </el-tabs>

    <div class="chart">
      <!-- No stats yet -->
      <EmptyState
        v-if="emptyState === 'noStats'"
        :title="i18n.t('noStatisticsYet')"
        :icon="ChartLine"
        :show-docs-btn="false"
        :show-create-btn="false" />

      <!-- No stats for the selected period -->
      <EmptyState
        v-else-if="emptyState === 'filteredEmpty'"
        :title="i18n.t('noStatisticsForPeriod')"
        :icon="ChartLine"
        :show-docs-btn="false"
        :show-create-btn="true"
        @create="resetFilters()">
        <template #create-btn>
          {{ i18n.t('resetFilters') }}
        </template>
      </EmptyState>

      <!-- Chart -->
      <InformationChart
        v-show="emptyState === 'none'"
        :key="chartKey"
        v-model:requested-range="requestedRange"
        :field="activeTabName"
        :chart-color="chartPrimaryColor"
        @empty-state-change="emptyState = $event" />
    </div>

    <!-- Control Button -->
    <div class="graph-control-button" @click="handleSettingsButtonClick">
      <Settings :size="20" class="text-contrast-muted" />
    </div>
  </el-card>
</template>

<script lang="ts" setup>
  import 'chartjs-plugin-style'

  import { useI18n } from 'vue-i18n'
  import { Settings, ChartLine } from 'lucide-vue-next'

  import { MetadataQueryField } from '@/client'
  import InformationChart from '@/components/charts/InformationChart.vue'

  const props = defineProps({
    applicationId: {
      type: String as PropType<string | undefined | null>,
      default: undefined,
    },
    hideTabs: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    chartType: {
      type: String as PropType<MetadataQueryField>,
      default: undefined,
    },
  })

  const i18n = useI18n()

  const settingsDialogOpen = ref(false)

  // Default is the last 90 days
  const defaultRange = () =>
    [new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), new Date()] as [
      Date,
      Date,
    ]

  // Range that should be requested from the server
  const requestedRange = ref<[Date, Date]>(defaultRange())

  function monthStart(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0)
  }

  function monthEnd(d: Date) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
  }

  // Normalize the requested range to cover full months
  watch(
    () => requestedRange.value,
    (range) => {
      if (!range?.[0] || !range?.[1]) return

      const normalizedRange: [Date, Date] = [
        monthStart(range[0]),
        monthEnd(range[1]),
      ]

      if (
        normalizedRange[0].getTime() !== range[0].getTime() ||
        normalizedRange[1].getTime() !== range[1].getTime()
      ) {
        requestedRange.value = normalizedRange
      }
    },
    { deep: true }
  )

  const activeTabName = ref<MetadataQueryField>(
    props.chartType || MetadataQueryField.ACCOUNT_AMOUNT
  )

  function getCssColorByName(name: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(name)
  }

  const chartPrimaryColor = computed(() => {
    if (activeTabName.value === 'account_amount') {
      return getCssColorByName('--sm-orange')
    } else if (activeTabName.value === 'software_amount') {
      return getCssColorByName('--sm-purple')
    } else if (activeTabName.value === 'licence_amount') {
      return getCssColorByName('--sm-red')
    } else return getCssColorByName('--sm-blue')
  })

  function handleSettingsButtonClick() {
    settingsDialogOpen.value = true
  }

  type EmptyStateType = 'none' | 'noStats' | 'filteredEmpty'

  const emptyState = ref<EmptyStateType>('none')
  const chartKey = ref(0)

  function resetFilters() {
    requestedRange.value = defaultRange()
    chartKey.value++
  }
</script>

<style lang="scss">
  .information-chart-card {
    height: 100%;

    .el-tabs__item {
      padding: 0 10px !important;
      height: 35px !important;

      &:hover {
        color: v-bind(chartPrimaryColor);
      }
    }

    .el-tabs__item.is-active {
      color: v-bind(chartPrimaryColor);
    }

    .el-tabs__active-bar {
      background-color: v-bind(chartPrimaryColor);
    }

    .chart {
      position: relative;
      height: calc(100% - 40px);
      width: 100%;

      canvas {
        height: 100%;
      }
    }
  }

  .graph-control-button {
    position: absolute;
    top: 10px;
    right: 5px;
    margin: 10px;
    padding: 5px;
    border-radius: 50%;

    cursor: pointer;
  }

  .close-btn {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    margin-top: 20px;
  }
</style>
